import { getCrossType } from './utilsDesign';

const TYPE_MAX = 5;
const PROFILE_MAX = 5;

/** Classic HD type pairing scores (symmetric), out of 5. */
const TYPE_SCORE = {
  G:  { G: 4, MG: 5, P: 5, M: 3, R: 3 },
  MG: { G: 5, MG: 3, P: 5, M: 3, R: 3 },
  P:  { G: 5, MG: 5, P: 2, M: 3, R: 4 },
  M:  { G: 3, MG: 3, P: 3, M: 2, R: 3 },
  R:  { G: 3, MG: 3, P: 4, M: 3, R: 2 },
};

/** Hexagram line harmonics used in HD profile chemistry. */
const LINE_HARMONIC = {
  1: 4,
  4: 1,
  2: 5,
  5: 2,
  3: 6,
  6: 3,
};

const statusFromScore = (score, maxScore) => {
  if (score >= Math.ceil(maxScore * 0.7)) return 'match';
  if (score >= Math.ceil(maxScore * 0.4)) return 'neutral';
  return 'mismatch';
};

export const getTypeCompatibility = (typeA, typeB) => {
  if (!typeA || !typeB) return null;
  const a = String(typeA).toUpperCase();
  const b = String(typeB).toUpperCase();
  const score = TYPE_SCORE[a]?.[b];
  if (typeof score !== 'number') return null;

  return {
    score,
    maxScore: TYPE_MAX,
    status: statusFromScore(score, TYPE_MAX),
    same: a === b,
  };
};

export const getProfileCompatibility = (profileA, profileB) => {
  if (!profileA?.length || !profileB?.length) return null;

  const a0 = Number(profileA[0]);
  const a1 = Number(profileA[1]);
  const b0 = Number(profileB[0]);
  const b1 = Number(profileB[1]);
  if (![a0, a1, b0, b1].every((n) => n >= 1 && n <= 6)) return null;

  const same = a0 === b0 && a1 === b1;
  if (same) {
    return {
      score: PROFILE_MAX,
      maxScore: PROFILE_MAX,
      status: 'match',
      same: true,
    };
  }

  const linesA = new Set([a0, a1]);
  const linesB = new Set([b0, b1]);
  let shared = 0;
  linesA.forEach((line) => {
    if (linesB.has(line)) shared += 1;
  });

  const harmonic =
    LINE_HARMONIC[a0] === b0
    || LINE_HARMONIC[a0] === b1
    || LINE_HARMONIC[a1] === b0
    || LINE_HARMONIC[a1] === b1;

  const angleA = getCrossType([a0, a1]);
  const angleB = getCrossType([b0, b1]);
  const angleComplement =
    (angleA === 'right' && angleB === 'left')
    || (angleA === 'left' && angleB === 'right');
  const bothJuxtaposition = angleA === 'juxtaposition' || angleB === 'juxtaposition';

  let score = 1;
  if (shared >= 1) score = 3;
  if (harmonic) score = Math.max(score, 4);
  if (angleComplement) score = Math.min(PROFILE_MAX, score + 1);
  if (bothJuxtaposition && score < 3) score = 3;

  return {
    score,
    maxScore: PROFILE_MAX,
    status: statusFromScore(score, PROFILE_MAX),
    same: false,
    shared,
    harmonic,
    angleComplement,
  };
};

export const getAuthorityCompatibility = (authA, authB) => {
  if (authA == null || authB == null) return null;
  const same = String(authA) === String(authB);
  return {
    score: same ? 1 : 0,
    maxScore: 1,
    status: same ? 'match' : 'neutral',
    same,
  };
};

const CROSS_MAX = 5;

/** Right (personal) × Left (transpersonal) is the classic complementary pairing. */
const ANGLE_SCORE = {
  right: { right: 3, left: 5, juxtaposition: 4 },
  left: { right: 5, left: 3, juxtaposition: 4 },
  juxtaposition: { right: 4, left: 4, juxtaposition: 3 },
};

export const getCrossAngleCompatibility = (profileA, profileB) => {
  const angleA = getCrossType(profileA);
  const angleB = getCrossType(profileB);
  if (!angleA || !angleB) return null;

  const score = ANGLE_SCORE[angleA]?.[angleB];
  if (typeof score !== 'number') return null;

  const complementary =
    (angleA === 'right' && angleB === 'left')
    || (angleA === 'left' && angleB === 'right');
  const hasJuxtaposition = angleA === 'juxtaposition' || angleB === 'juxtaposition';

  return {
    score,
    maxScore: CROSS_MAX,
    status: statusFromScore(score, CROSS_MAX),
    same: angleA === angleB,
    complementary,
    hasJuxtaposition,
    angleA,
    angleB,
  };
};
